/**
 * What a customer may actually choose, and what it costs.
 *
 * WHAT WAS WRONG
 * --------------
 * A modifier option was looked up by its id alone and its price added to the
 * line. Nothing checked that the option had anything to do with the dish.
 *
 * That is a hole with money on the other side of it, and it does not need a
 * malicious option to open it — an ordinary one does. "Small: −150" is a
 * perfectly normal thing for a size group to contain. Applied to a dish it was
 * never attached to, it takes Rs. 150 off. Proved against the running system:
 * a Rs. 9,999 pizza quoted at Rs. 9,849 using a size option belonging to
 * nothing, with no approval asked for, no discount limit consulted, and no
 * discount recorded anywhere — because as far as the system was concerned it
 * was not a discount at all.
 *
 * Sending the same option three times took off Rs. 450. A hundred times takes
 * the bill below zero, which is the negative-total problem the discount limit
 * was built to prevent, arriving through a door that had no lock on it.
 *
 * WHY THE GROUP'S OWN RULES ARE THE CONTROL
 * -----------------------------------------
 * `min_selections`, `max_selections` and `is_required` have been in the schema
 * from the beginning and no code has ever read one. That is the same fault as
 * `max_discount_without_approval`, which sat there saying 10 while nothing
 * enforced it: a column that reads like a control and is not one is worse than
 * no column, because everybody assumes it is working.
 *
 * WHY IT REFUSES RATHER THAN SILENTLY DROPPING
 * --------------------------------------------
 * The old code ignored an option it could not find. A cashier taps "extra
 * cheese", the kitchen never hears about it, the customer is not charged, and
 * nothing anywhere looks wrong. Refusing is noisier and correct: the till can
 * say what is wrong while the customer is still standing there.
 *
 * WHY IT IS SHARED RATHER THAN WRITTEN TWICE
 * ------------------------------------------
 * The till has to know what is allowed before it sends anything, or it shows a
 * picker that produces a refusal — and the server has to check anyway, because
 * a till is a browser and anything it sends can be edited. Two copies of a
 * rule drift, and the way this one would drift is a screen that lets somebody
 * finish an order the server then rejects.
 *
 * The server remains the authority. This is the same rule, read twice.
 */

/**
 * The fewest choices a group will accept.
 *
 * `is_required` and `min_selections` say the same thing in two ways and a
 * group can carry either, so the stricter of the two applies.
 *
 * It exists as a function because it was written out by hand in three places —
 * the checker, the till's picker, and the screen that describes a group — and
 * one of the three got it wrong: `required ? 1 : min` reads a group that is
 * required *and* asks for two as asking for one. The till then let a cashier
 * untick down to one and the server refused the order.
 *
 * That is the exact drift this rule was moved into the shared package to
 * prevent, and it still happened, in the one place the shared function was not
 * the thing being called.
 */
export function fewestAllowed(group) {
  const min = Number(group.min_selections) || 0;
  return group.is_required ? Math.max(min, 1) : min;
}

/**
 * Check the choices made against one dish, and total them.
 *
 * Pure — it is given the groups rather than fetching them, so the rules can be
 * tested without a database and the caller can fetch once for a whole order.
 *
 * @param {object}  product          the dish, for the message
 * @param {Array}   groups           groups attached to it, each with .options
 * @param {Array}   chosen           [{ optionId }] as sent by the till
 * @returns {{ error?: string, total?: number, modifiers?: Array }}
 */
export function checkModifiers(product, groups, chosen = []) {
  const dish = product?.name ?? 'that dish';

  /** Every option this dish actually offers, and the group it came from. */
  const offered = new Map();
  for (const group of groups) {
    for (const option of group.options ?? []) {
      offered.set(option.id, { option, group });
    }
  }

  const seen = new Set();
  const pickedFrom = new Map();
  const modifiers = [];
  let total = 0;

  for (const pick of chosen) {
    const found = offered.get(pick.optionId);

    /**
     * Named as "not offered on this dish" rather than "not found", because
     * both cases arrive here and the difference does not help whoever is at
     * the till. What they need to know is that this dish does not have it.
     */
    if (!found) {
      return { error: `That choice is not offered on ${dish}.` };
    }

    /**
     * The same option twice is the cheap version of the attack, and it is
     * also just a double tap on a touchscreen. Either way it is not a
     * different choice, so it is not charged twice.
     */
    if (seen.has(pick.optionId)) {
      return { error: `${found.option.name} was chosen twice on ${dish}.` };
    }
    seen.add(pick.optionId);

    pickedFrom.set(found.group.id, (pickedFrom.get(found.group.id) || 0) + 1);
    total += Number(found.option.price_adjustment) || 0;
    modifiers.push({
      optionId: found.option.id,
      name: found.option.name,
      priceAdjustment: Number(found.option.price_adjustment) || 0,
    });
  }

  for (const group of groups) {
    const count = pickedFrom.get(group.id) || 0;
    const max = Number(group.max_selections);

    /**
     * A maximum of zero or less would mean the group can never be chosen
     * from, which is not something anybody means to configure. Treated as
     * "no limit" rather than as "nothing allowed", so a mis-set group does
     * not silently make a dish unsellable.
     */
    if (max > 0 && count > max) {
      return {
        error:
          max === 1
            ? `Only one ${group.name.toLowerCase()} can be chosen on ${dish}.`
            : `At most ${max} from ${group.name} can be chosen on ${dish}.`,
      };
    }

    const floor = fewestAllowed(group);

    if (count < floor) {
      return {
        error:
          floor === 1
            ? `${dish} needs a choice from ${group.name}.`
            : `${dish} needs ${floor} choices from ${group.name}.`,
      };
    }
  }

  return { total, modifiers };
}
